import React, { useMemo } from 'react';

import { dashboardDeliveryRequestApis } from '@apis/next/deliveryRequestApis';
import { fileUploadApis } from '@apis/next/fileUploadApis';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogTitle,
} from '@components/Dialog';
import { FileInput } from '@components/NextForms/FileInput';
import { IndexedLabel } from '@components/NextForms/IndexedForm';
import { IndexedFormContainer } from '@components/NextForms/IndexedForm';
import { TextInput } from '@components/NextForms/TextInput';
import {
  deliveryRequestStrings,
  resolveDeliveryRequestBatchSentErrors,
} from '@data/deliveryRequest';
import { useDialogKey } from '@hooks/useDialogKey';
import { useFormUtils } from '@hooks/useFormUtils';
import {
  DeliveryRequestBatchSentForm,
  DeliveryRequestListItem,
} from '@models/next/DeliveryRequest';
import { DeliveryRequestHelper } from '@models/next/DeliveryRequestHelper';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';

const strings = createStrings({
  ja: {
    title: deliveryRequestStrings.deliverySentTitle,
    cannotBatchSubmitDeliveryRequest: `下記の原因で処理できません：`,
    upload: 'Upload',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialogContent: {
    ...common.flexColumn,
    rowGap: '1rem',
    padding: '2.5rem 2rem',
    flexShrink: 1,
    minHeight: 0,
    maxHeight: 550,
  },
}));

const DeliveryRequestBatchSentDialogContent: React.FC<
  Required<DeliveryRequestBatchSentDialogProps>
> = ({ onClose, deliveryRequests, type }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const postFileUpload = fileUploadApis.usePost('/file/upload');

  const queryClient = useQueryClient();

  const putDeliveryRequest = dashboardDeliveryRequestApis.usePut(
    '/dashboard/delivery-request',
    {
      queries: {
        id: deliveryRequests.map((d) => d.id),
        action: 'BATCH_SENT',
      },
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(
          dashboardDeliveryRequestApis.getKeyByPath('get', '/dashboard/delivery-request'),
        );
      },
    },
  );

  const form = useForm(DeliveryRequestBatchSentForm, {
    initialOutput: DeliveryRequestHelper.getBatchSentFormInitialOutput(),
  });

  const { fields } = form.root;

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [postFileUpload, putDeliveryRequest],
  });

  return (
    <form
      {...form.bindForm({
        async onSubmit(data) {
          const [delivery_sent_file] = data.delivery_sent_file
            ? await postFileUpload.mutateAsync({
                file: [data.delivery_sent_file.file],
              })
            : [];

          await putDeliveryRequest.mutateAsync(
            DeliveryRequestHelper.mapBatchSentFormToBatchSubmitBody(data, { delivery_sent_file }),
          );

          onClose();
        },
      })}
    >
      <div className={classes.dialogContent}>
        <IndexedFormContainer size="md">
          <div className={classes.colSpanFull}>{renderAlertsAndSpinnerModal()}</div>

          <IndexedLabel field={fields.time_date} />
          <div>
            <TextInput field={fields.time_date} />
          </div>

          <IndexedLabel field={fields.time_time} />
          <div>
            <TextInput field={fields.time_time} />
          </div>

          {type === 'single' && (
            <>
              <IndexedLabel field={fields.delivery_sent_file} />
              <div>
                <FileInput field={fields.delivery_sent_file} />
              </div>
            </>
          )}
        </IndexedFormContainer>
      </div>

      <ActionableDialogActions justifyContent="right">
        <Button
          color="outlined"
          size="small"
          text={formStrings.cancel}
          type="button"
          onClick={onClose}
        />
        <Button color="primary" size="small" text={deliveryRequestStrings.deliverySent} />
      </ActionableDialogActions>
    </form>
  );
};

export interface DeliveryRequestBatchSentDialogProps {
  deliveryRequests: DeliveryRequestListItem[];
  open: boolean;
  onClose: () => void;
  type: 'batch' | 'single';
}

export const DeliveryRequestBatchSentDialog: React.FC<DeliveryRequestBatchSentDialogProps> = (
  props,
) => {
  const { deliveryRequests, open, onClose, type } = props;

  const dialogKey = useDialogKey(open);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const batchSentErrorsResult = useMemo(
    () => resolveDeliveryRequestBatchSentErrors(deliveryRequests),
    [deliveryRequests],
  );

  const renderErrors = () => {
    if (batchSentErrorsResult._tag === 'Left') {
      return (
        <>
          <div
            className={clsx(classes.middleText, classes.textGray500)}
            style={{ padding: '2rem' }}
          >
            {strings.cannotBatchSubmitDeliveryRequest}

            <ul>
              {batchSentErrorsResult.left.messages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>

            <Button
              color="primary"
              style={{ width: 100, marginTop: 50 }}
              text="OK"
              size="small"
              onClick={onClose}
            />
          </div>
        </>
      );
    }
  };

  return (
    <ActionableDialog open={open} onClose={onClose} size="md" source={import.meta.url}>
      <ActionableDialogTitle>{strings.title}</ActionableDialogTitle>

      {batchSentErrorsResult._tag === 'Right' && (
        <DeliveryRequestBatchSentDialogContent key={dialogKey} {...props} type={type} />
      )}

      {renderErrors()}
    </ActionableDialog>
  );
};
